import React, { useEffect, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  FaceSmileIcon,
  MapPinIcon,
  SparklesIcon,
} from '@heroicons/react/24/solid';
import { getAdminAuthHeaders } from '../services/adminApi';
import { useAuth } from '../AuthContext';

const API_BASE_URL = `${process.env.REACT_APP_GOTTEA_API_BASE_URL || 'https://gottea-search-5gtrn.ondigitalocean.app'}/api/v1/surveys/analytics`;

const GROUP_OPTIONS = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
];

const FINAL_SCORE_MAX = 21;
const QUESTION_SCORE_MAX = 3;

function getIndiaDateParts(date = new Date()) {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const parts = formatter.formatToParts(date);
  const getPart = (type) => parts.find((part) => part.type === type)?.value || '';

  return {
    year: getPart('year'),
    month: getPart('month'),
    day: getPart('day'),
  };
}

function getDefaultDateRange() {
  const indiaToday = getIndiaDateParts();

  return {
    startDate: `${indiaToday.year}-${indiaToday.month}-01`,
    endDate: `${indiaToday.year}-${indiaToday.month}-${indiaToday.day}`,
  };
}

function formatDateLabel(value) {
  if (!value) {
    return 'NA';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

function formatDateTimeLabel(value) {
  if (!value) {
    return 'NA';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatPercent(value) {
  if (value === null || value === undefined) {
    return 'NA';
  }

  return `${Number(value).toFixed(2)}%`;
}

function formatFinalScore(value) {
  if (value === null || value === undefined) {
    return 'NA';
  }

  return `${Number(value).toFixed(2)} / ${FINAL_SCORE_MAX}`;
}

function formatQuestionScore(value) {
  if (value === null || value === undefined) {
    return 'NA';
  }

  return `${Number(value).toFixed(2)} / ${QUESTION_SCORE_MAX}`;
}

function toFinalScorePercent(value) {
  if (value === null || value === undefined) {
    return 0;
  }

  const safeValue = Math.max(0, Math.min(Number(value) || 0, FINAL_SCORE_MAX));
  return (safeValue / FINAL_SCORE_MAX) * 100;
}

function formatFinalScorePercent(value) {
  const percent = toFinalScorePercent(value);
  const truncatedPercent = Math.trunc(percent * 100) / 100;
  return `${truncatedPercent.toFixed(2)}%`;
}

function StatCard({ title, value, helper, icon: Icon, accent }) {
  return (
    <div className="rounded-3xl border border-white/70 bg-white/90 p-5 shadow-sm backdrop-blur">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <p className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">{value}</p>
        </div>
        <div className={`rounded-2xl p-3 ${accent}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <p className="text-sm text-slate-500">{helper}</p>
    </div>
  );
}

function SectionCard({ title, subtitle, action, children }) {
  return (
    <section className="rounded-[28px] border border-white/70 bg-white/90 p-6 shadow-sm backdrop-blur">
      <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
          {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function HorizontalBarList({ items, valueKey, labelKey, formatter = (value) => value, colorClass = 'bg-teal-500' }) {
  const maxValue = Math.max(...items.map((item) => item[valueKey] || 0), 1);

  return (
    <div className="space-y-4">
      {items.map((item) => {
        const rawValue = item[valueKey] || 0;
        const width = `${(rawValue / maxValue) * 100}%`;

        return (
          <div key={`${item[labelKey]}-${rawValue}`}>
            <div className="mb-2 flex items-center justify-between gap-4 text-sm">
              <span className="font-medium text-slate-700">{item[labelKey]}</span>
              <span className="text-slate-500">{formatter(rawValue)}</span>
            </div>
            <div className="h-3 overflow-hidden rounded-full bg-slate-100">
              <div className={`h-full rounded-full ${colorClass}`} style={{ width }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MysteryReports() {
  const { hasPermission } = useAuth();
  const defaultDates = getDefaultDateRange();
  const [filters, setFilters] = useState({
    startDate: defaultDates.startDate,
    endDate: defaultDates.endDate,
    groupBy: 'week',
    location: '',
  });
  const [analytics, setAnalytics] = useState(null);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    const loadAnalytics = async () => {
      setLoading(true);
      setError('');

      try {
        const params = new URLSearchParams({
          groupBy: filters.groupBy,
          startDate: filters.startDate,
          endDate: filters.endDate,
        });

        if (filters.location) {
          params.set('location', filters.location);
        }

        const response = await fetch(`${API_BASE_URL}?${params.toString()}`, {
          signal: controller.signal,
          headers: await getAdminAuthHeaders(),
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const payload = await response.json();

        if (!payload.success) {
          throw new Error(payload.message || 'Analytics request failed');
        }

        setAnalytics(payload.data);
        setAvailableLocations(payload.data?.filtersMetadata?.availableLocations || []);
      } catch (fetchError) {
        if (fetchError.name !== 'AbortError') {
          setError(fetchError.message || 'Failed to load mystery reports');
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadAnalytics();
    return () => controller.abort();
  }, [filters.startDate, filters.endDate, filters.groupBy, filters.location, refreshToken]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleCsvDownload = async () => {
    const params = new URLSearchParams({
      format: 'csv',
      startDate: filters.startDate,
      endDate: filters.endDate,
    });

    if (filters.location) {
      params.set('location', filters.location);
    }

    try {
      const response = await fetch(`${API_BASE_URL}?${params.toString()}`, { headers: await getAdminAuthHeaders() });
      if (!response.ok) throw new Error('Failed to export mystery reports.');
      const url = URL.createObjectURL(await response.blob());
      const anchor = document.createElement('a');
      anchor.href = url; anchor.download = 'mystery-reports.csv'; anchor.click(); URL.revokeObjectURL(url);
    } catch (downloadError) { setError(downloadError.message); }
  };

  const summary = analytics?.summary;
  const highlights = analytics?.highlights;
  const charts = analytics?.charts;
  const tables = analytics?.tables;

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(71,144,139,0.26),_transparent_34%),linear-gradient(180deg,_#e7f1ef_0%,_#fffef7_46%,_#edf6f4_100%)]">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-8 overflow-hidden rounded-[32px] border border-white/70 bg-[#47908B] text-white shadow-xl">
          <div className="grid gap-8 px-6 py-8 md:grid-cols-[1.4fr_0.9fr] md:px-8">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-teal-100">
                <SparklesIcon className="h-4 w-4" />
                Mystery Reports
              </div>
              <h1 className="mt-4 max-w-2xl text-3xl font-semibold tracking-tight sm:text-4xl">
                Survey analytics for field-level cafe experience trends.
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300 sm:text-base">
                Track submission volume, score quality, weak touchpoints, and location-level issues from the mystery audit feed.
              </p>
            </div>

            <div className="grid gap-4 rounded-[28px] border border-white/10 bg-[rgba(28,68,66,0.55)] p-5 backdrop-blur">
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="text-sm">
                  <span className="mb-2 block text-slate-300">Start date</span>
                  <input
                    type="date"
                    name="startDate"
                    value={filters.startDate}
                    onChange={handleFilterChange}
                    className="w-full rounded-2xl border border-white/20 bg-[rgba(20,52,50,0.55)] px-4 py-3 text-white outline-none transition focus:border-teal-200"
                  />
                </label>
                <label className="text-sm">
                  <span className="mb-2 block text-slate-300">End date</span>
                  <input
                    type="date"
                    name="endDate"
                    value={filters.endDate}
                    onChange={handleFilterChange}
                    className="w-full rounded-2xl border border-white/20 bg-[rgba(20,52,50,0.55)] px-4 py-3 text-white outline-none transition focus:border-teal-200"
                  />
                </label>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="text-sm">
                  <span className="mb-2 block text-slate-300">Location</span>
                  <select
                    name="location"
                    value={filters.location}
                    onChange={handleFilterChange}
                    className="w-full rounded-2xl border border-white/20 bg-[rgba(20,52,50,0.55)] px-4 py-3 text-white outline-none transition focus:border-teal-200"
                  >
                    <option value="">All locations</option>
                    {availableLocations.map((location) => (
                      <option key={location} value={location}>
                        {location}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm">
                  <span className="mb-2 block text-slate-300">Group by</span>
                  <select
                    name="groupBy"
                    value={filters.groupBy}
                    onChange={handleFilterChange}
                    className="w-full rounded-2xl border border-white/20 bg-[rgba(20,52,50,0.55)] px-4 py-3 text-white outline-none transition focus:border-teal-200"
                  >
                    {GROUP_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => setRefreshToken((current) => current + 1)}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-teal-500 px-4 py-3 text-sm font-semibold text-white transition hover:opacity-90"
                >
                  <ArrowPathIcon className="h-4 w-4" />
                  Refresh data
                </button>
                {hasPermission('reports.mystery.export') && <button
                  type="button"
                  onClick={handleCsvDownload}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/15"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  Download CSV
                </button>}
              </div>
            </div>
          </div>
        </div>

        {error ? (
          <div className="rounded-3xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="flex min-h-[320px] items-center justify-center">
            <div className="rounded-3xl border border-white/70 bg-white/80 px-6 py-5 text-slate-600 shadow-sm">
              Loading mystery report analytics...
            </div>
          </div>
        ) : analytics ? (
          <div className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
              <StatCard
                title="Total submissions"
                value={summary?.totalSubmissions ?? 0}
                helper={`Last submission ${formatDateTimeLabel(summary?.lastSubmissionAt)}`}
                icon={ChartBarIcon}
                accent="bg-teal-50 text-teal-600"
              />
              <StatCard
                title="Average final score"
                value={formatFinalScore(summary?.averageFinalScore)}
                helper={`Weighted score across all survey submissions out of ${FINAL_SCORE_MAX}`}
                icon={SparklesIcon}
                accent="bg-amber-50 text-amber-600"
              />
              <StatCard
                title="Average question score"
                value={formatQuestionScore(summary?.averageQuestionScore)}
                helper={`Average per-question rating on a ${QUESTION_SCORE_MAX}-point scale`}
                icon={FaceSmileIcon}
                accent="bg-cyan-50 text-cyan-600"
              />
              <StatCard
                title="Positive rating share"
                value={formatPercent(summary?.positiveRatingShare)}
                helper={`${summary?.positiveRatingsCount ?? 0} positive ratings logged`}
                icon={CalendarDaysIcon}
                accent="bg-emerald-50 text-emerald-600"
              />
              <StatCard
                title="Comments captured"
                value={summary?.commentsCount ?? 0}
                helper={`${summary?.uniqueLocations ?? 0} locations represented`}
                icon={ChatBubbleLeftRightIcon}
                accent="bg-rose-50 text-rose-600"
              />
            </div>

            <div className="grid gap-6 xl:grid-cols-2">
              <SectionCard title="Key highlights" subtitle="Top and bottom performers from the active filter set.">
                <div className="grid gap-4 lg:grid-cols-2">
                  <div className="rounded-3xl bg-emerald-50 p-5">
                    <p className="text-sm font-medium text-emerald-700">Top location</p>
                    <h4 className="mt-2 text-xl font-semibold text-slate-900">{highlights?.topLocation?.location || 'NA'}</h4>
                    <p className="mt-1 text-sm text-slate-600">
                      {highlights?.topLocation?.submissions ?? 0} submissions, avg final score {formatFinalScore(highlights?.topLocation?.averageFinalScore)}
                    </p>
                  </div>
                  <div className="rounded-3xl bg-rose-50 p-5">
                    <p className="text-sm font-medium text-rose-700">Lowest location</p>
                    <h4 className="mt-2 text-xl font-semibold text-slate-900">{highlights?.lowestLocation?.location || 'NA'}</h4>
                    <p className="mt-1 text-sm text-slate-600">
                      {highlights?.lowestLocation?.submissions ?? 0} submissions, avg final score {formatFinalScore(highlights?.lowestLocation?.averageFinalScore)}
                    </p>
                  </div>
                  <div className="rounded-3xl bg-cyan-50 p-5">
                    <p className="text-sm font-medium text-cyan-700">Strongest question</p>
                    <h4 className="mt-2 text-base font-semibold text-slate-900">{highlights?.strongestQuestion?.question || 'NA'}</h4>
                    <p className="mt-1 text-sm text-slate-600">
                      Avg {formatQuestionScore(highlights?.strongestQuestion?.averageScore)} with {highlights?.strongestQuestion?.highScoreCount ?? 0} high scores
                    </p>
                  </div>
                  <div className="rounded-3xl bg-amber-50 p-5">
                    <p className="text-sm font-medium text-amber-700">Weakest question</p>
                    <h4 className="mt-2 text-base font-semibold text-slate-900">{highlights?.weakestQuestion?.question || 'NA'}</h4>
                    <p className="mt-1 text-sm text-slate-600">
                      Avg {formatQuestionScore(highlights?.weakestQuestion?.averageScore)} with {highlights?.weakestQuestion?.lowScoreCount ?? 0} low scores
                    </p>
                  </div>
                </div>
              </SectionCard>

              <SectionCard title="Submissions over time" subtitle="Volume and average final score by selected aggregation.">
                <div className="space-y-4">
                  {(charts?.submissionsOverTime || []).map((item) => (
                    <div key={item.period} className="rounded-3xl border border-slate-100 p-4">
                      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{formatDateLabel(item.period)}</p>
                          <p className="text-xs text-slate-500">{item.submissions} submissions</p>
                        </div>
                        <div className="text-right text-sm text-slate-600">
                          <div>Final score {formatFinalScore(item.averageFinalScore)}</div>
                          <div>Question score {formatQuestionScore(item.averageQuestionScore)}</div>
                        </div>
                      </div>
                      <div className="h-3 overflow-hidden rounded-full bg-slate-100">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-teal-500 to-cyan-500"
                          style={{
                            width: `${toFinalScorePercent(item.averageFinalScore)}%`,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>
            </div>

            <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
              <SectionCard title="Rating breakdown" subtitle="Distribution of overall customer sentiment.">
                <HorizontalBarList
                  items={charts?.ratingBreakdown || []}
                  labelKey="rating"
                  valueKey="count"
                  formatter={(value) => `${value} responses`}
                />
              </SectionCard>

              <SectionCard title="Final score distribution" subtitle="How submissions spread across score bands.">
                <HorizontalBarList
                  items={charts?.finalScoreDistribution || []}
                  labelKey="label"
                  valueKey="count"
                  formatter={(value) => `${value} submissions`}
                  colorClass="bg-[#2f6f6b]"
                />
              </SectionCard>
            </div>

            <div>
              <SectionCard title="Weekday heatmap" subtitle="Response volume and score quality by day of week.">
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {(charts?.weekdayHeatmap || []).map((day) => (
                    <div key={day.dayOfWeek} className="rounded-3xl border border-slate-100 bg-slate-50 p-5">
                      <div>
                        <p className="text-xl font-semibold text-slate-900">{day.dayOfWeek}</p>
                        <p className="mt-3 text-5xl font-semibold leading-none text-slate-900">{day.submissions}</p>
                        <p className="mt-2 text-sm text-slate-500">submissions</p>
                      </div>
                      <div className="mt-5 rounded-2xl bg-white px-4 py-3 shadow-sm">
                        <div className="text-xs font-medium uppercase tracking-[0.18em] text-slate-400">Avg score</div>
                        <div className="mt-2 text-2xl font-semibold text-slate-900">{formatFinalScore(day.averageFinalScore)}</div>
                      </div>
                      <div className="mt-5">
                        <div className="mb-2 flex items-center justify-between text-xs text-slate-500">
                          <span>Score strength</span>
                          <span>{formatFinalScorePercent(day.averageFinalScore)}</span>
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-white">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-[#47908B] to-[#2f6f6b]"
                            style={{
                              width: `${toFinalScorePercent(day.averageFinalScore)}%`,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>
            </div>

            <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
              <SectionCard
                title="Location table"
                subtitle="Per-location submission and score summary."
                action={
                  <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                    <MapPinIcon className="h-4 w-4" />
                    {tables?.locations?.length ?? 0} locations
                  </div>
                }
              >
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className="text-slate-500">
                      <tr>
                        <th className="pb-3 pr-4 font-medium">Location</th>
                        <th className="pb-3 pr-4 font-medium">Submissions</th>
                        <th className="pb-3 pr-4 font-medium">Avg final</th>
                        <th className="pb-3 font-medium">Comments</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(tables?.locations || []).map((row) => (
                        <tr key={row.location} className="border-t border-slate-100">
                          <td className="py-3 pr-4 font-medium text-slate-900">{row.location}</td>
                          <td className="py-3 pr-4 text-slate-600">{row.submissions}</td>
                          <td className="py-3 pr-4 text-slate-600">{formatFinalScore(row.averageFinalScore)}</td>
                          <td className="py-3 text-slate-600">{row.commentsCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>

              <SectionCard title="Question table" subtitle="Questions with low-score pressure points.">
                <div className="space-y-3">
                  {(tables?.questions || []).map((question) => (
                    <div key={question.id} className="rounded-3xl border border-slate-100 p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{question.question}</p>
                          <p className="mt-1 text-xs text-slate-500">
                            {question.responseCount} responses, {question.lowScoreCount} low scores
                          </p>
                        </div>
                        <div className="rounded-2xl bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700">
                          Avg {formatQuestionScore(question.averageScore)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>
            </div>

            <SectionCard
              title="Recent comments"
              subtitle="Latest qualitative notes from survey submissions."
              action={
                <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                  <ClockIcon className="h-4 w-4" />
                  {tables?.recentComments?.length ?? 0} comments shown
                </div>
              }
            >
              <div className="grid gap-4 lg:grid-cols-2">
                {(tables?.recentComments || []).map((comment) => (
                  <article key={comment.id} className="rounded-3xl border border-slate-100 bg-slate-50 p-5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                        {comment.location}
                      </span>
                      <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                        {comment.rating}
                      </span>
                      <span className="rounded-full bg-white px-3 py-1 text-xs text-slate-500">
                        Score {formatFinalScore(comment.finalScore)}
                      </span>
                    </div>
                    <p className="mt-4 text-sm leading-6 text-slate-700">{comment.comment}</p>
                    <p className="mt-4 text-xs text-slate-500">
                      Visit {comment.visitDate} at {comment.visitTime} • Logged {formatDateTimeLabel(comment.createdAt)}
                    </p>
                  </article>
                ))}
              </div>
            </SectionCard>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default MysteryReports;
